import { API_BASE_URL } from '../config';
import type { FamilyMemberApi } from '../components/employee/fillingForms/chat/family/familyInfoTypes';

export type GeneralProfileValues = {
  familyName?: string;
  firstName?: string;
  middleName?: string;
  familyNameKana?: string;
  firstNameKana?: string;
  middleNameKana?: string;
  dateOfBirth?: string;
  gender?: string;
  nationality?: string;
  phoneNumber?: string;
  homePhoneNumber?: string;
  postalCode?: string;
  prefecture?: string;
  city?: string;
  streetAddress?: string;
  buildingName?: string;
  roomNumber?: string;
  emailAddress?: string;
  residentCardNumber?: string;
  myNumber?: string;
  maritalStatus?: string;
  isWidowed?: boolean;
  isSingleParent?: boolean;
  isWorkingStudent?: boolean;
  disabilityCategory?: string;
  emergencyContactName?: string;
  emRelationship?: string;
  emergencyContactPhone?: string;
  emergencyPostalCode?: string;
  emergencyPrefecture?: string;
  emergencyCity?: string;
  emergencyStreetAddress?: string;
  emergencyBuildingName?: string;
  emergencyRoomNumber?: string;
};

export type AdminGeneralProfileResponse = {
  email?: string;
  invitationId?: string;
  status?: string;
  signatureCaptured?: boolean;
  generalInfoAdminCorrectedBy?: string;
  generalInfoAdminCorrectedAt?: string;
  values?: GeneralProfileValues;
};

export type AdminFamilyProfileResponse = {
  email?: string;
  invitationId?: string;
  familyInfoStatus?: string;
  members?: FamilyMemberApi[];
  familyInfoAdminCorrectedBy?: string;
  familyInfoAdminCorrectedAt?: string;
};

const ADMIN_EMAIL_KEY = 'admin-email';

export function getAdminCorrectedBy(): string {
  return localStorage.getItem(ADMIN_EMAIL_KEY)?.trim() || 'admin';
}

export function setAdminEmail(email: string): void {
  const trimmed = email.trim();
  if (trimmed) {
    localStorage.setItem(ADMIN_EMAIL_KEY, trimmed);
  }
}

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const base = API_BASE_URL?.trim();
  if (!base) throw new Error('API unavailable');
  const res = await fetch(`${base}${path}`, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export function fetchAdminGeneralProfile(
  email: string,
  invitationId?: string
): Promise<AdminGeneralProfileResponse> {
  const q = new URLSearchParams({ email: email.trim() });
  if (invitationId?.trim()) q.set('invitationId', invitationId.trim());
  return adminFetch(`/api/admin/check-submissions/general-profile?${q}`);
}

export function updateAdminGeneralProfile(body: {
  email: string;
  invitationId?: string;
  values: GeneralProfileValues;
  correctedBy?: string;
}): Promise<AdminGeneralProfileResponse> {
  return adminFetch('/api/admin/check-submissions/general-profile', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...body,
      correctedBy: body.correctedBy ?? getAdminCorrectedBy(),
    }),
  });
}

export function fetchAdminFamilyInfo(
  email: string,
  invitationId?: string
): Promise<AdminFamilyProfileResponse> {
  const q = new URLSearchParams({ email: email.trim() });
  if (invitationId?.trim()) q.set('invitationId', invitationId.trim());
  return adminFetch(`/api/admin/check-submissions/family-info?${q}`);
}

export function updateAdminFamilyInfo(body: {
  email: string;
  invitationId?: string;
  members: FamilyMemberApi[];
  correctedBy?: string;
}): Promise<AdminFamilyProfileResponse> {
  return adminFetch('/api/admin/check-submissions/family-info', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...body,
      correctedBy: body.correctedBy ?? getAdminCorrectedBy(),
    }),
  });
}

export async function fetchAdminSignaturePreviewUrl(email: string): Promise<string | null> {
  const base = API_BASE_URL?.trim();
  const emailTrim = email.trim();
  if (!base || !emailTrim) return null;
  const profile = await fetchAdminGeneralProfile(emailTrim);
  if (!profile.signatureCaptured) return null;
  const imgRes = await fetch(`${base}/api/general-profile/signature?email=${encodeURIComponent(emailTrim)}`);
  if (!imgRes.ok) return null;
  const blob = await imgRes.blob();
  if (!blob.size) return null;
  return URL.createObjectURL(blob);
}
